package com.zq.webproject.ui.invite

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.wrapContentSize
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.ImageBitmap
import kotlinx.coroutines.launch
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Download
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import io.github.alexzhirkevich.qrose.options.QrBallShape
import io.github.alexzhirkevich.qrose.options.QrBrush
import io.github.alexzhirkevich.qrose.options.QrFrameShape
import io.github.alexzhirkevich.qrose.options.QrPixelShape
import io.github.alexzhirkevich.qrose.options.circle
import io.github.alexzhirkevich.qrose.options.roundCorners
import io.github.alexzhirkevich.qrose.options.solid
import io.github.alexzhirkevich.qrose.rememberQrCodePainter
import org.jetbrains.compose.resources.Font
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import webproject.composeapp.generated.resources.Res
import webproject.composeapp.generated.resources.font_song
import webproject.composeapp.generated.resources.icon_bg

/**
 * 平台特定的截图保存功能
 */
@Composable
expect fun SaveScreenshotEffect(
    bitmap: ImageBitmap?,
    onSaveComplete: (Result<String>) -> Unit
)

/**
 * 平台特定的界面包装器（支持截图）
 * @param shouldCapture 是否需要触发截图
 * @param onCaptureComplete 截图完成后的回调
 */
@Composable
expect fun CapturableContent(
    modifier: Modifier,
    shouldCapture: Boolean,
    onCaptureComplete: (ImageBitmap) -> Unit,
    content: @Composable () -> Unit
)

// 统一字体（使用宋体，包含完整中文字符）
private val CaiFont: FontFamily
    @Composable
    get() = FontFamily(Font(Res.font.font_song))

/**
 * 邀请页面
 */
@Preview
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun InviteScreen(
    onBackClick: () -> Unit = {}
) {
    println("InviteScreen - 渲染邀请页面") // 日志输出

    val viewModel = remember { InviteViewModel() }
    val snackbarHostState = remember { SnackbarHostState() }
    val coroutineScope = rememberCoroutineScope()
    
    // 用于保存的 bitmap 状态
    var capturedBitmap by remember { mutableStateOf<ImageBitmap?>(null) }
    var captureRequest by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(false) }
    
    // 保存截图
    SaveScreenshotEffect(capturedBitmap) { result ->
        coroutineScope.launch {
            result.onSuccess { message ->
                println("InviteScreen - $message")
                snackbarHostState.showSnackbar(
                    message = message,
                    duration = SnackbarDuration.Short
                )
            }.onFailure { error ->
                println("InviteScreen - 保存失败: ${error.message}")
                snackbarHostState.showSnackbar(
                    message = "保存失败: ${error.message}",
                    duration = SnackbarDuration.Short
                )
            }
            // 重置状态
            isLoading = false
            capturedBitmap = null
        }
    }

    Scaffold(
        snackbarHost = {
            SnackbarHost(hostState = snackbarHostState)
        },
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "邀请好友",
                        fontFamily = CaiFont,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = {
                        println("InviteScreen - 点击返回按钮") // 日志输出
                        onBackClick()
                    }) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "返回"
                        )
                    }
                },
                actions = {
                    // 保存按钮
                    IconButton(onClick = {
                        println("InviteScreen - 点击保存按钮")
                        println("InviteScreen - 当前 captureRequest = $captureRequest")
                        captureRequest = true
                        println("InviteScreen - 设置后 captureRequest = $captureRequest")
                    }) {
                        Icon(
                            imageVector = Icons.Default.Download,
                            contentDescription = "保存到本地",
                            tint = Color.White
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color(0xFF1E88E5),
                    titleContentColor = Color.White,
                    navigationIconContentColor = Color.White
                )
            )
        }
    ) { paddingValues ->
        Box(modifier = Modifier.fillMaxSize()) {
            CapturableContent(
                modifier = Modifier.fillMaxSize(),
                shouldCapture = captureRequest,
                onCaptureComplete = { bitmap ->
                    println("InviteScreen - onCaptureComplete 被调用，bitmap尺寸: ${bitmap.width}x${bitmap.height}")
                    // 截图完成，显示加载弹框并触发保存
                    isLoading = true
                    capturedBitmap = bitmap
                    captureRequest = false
                }
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(paddingValues)
                        .verticalScroll(rememberScrollState())
                ) {
                    // 背景图
                    Image(
                        painterResource(Res.drawable.icon_bg),
                        contentDescription = null,
                        contentScale = ContentScale.FillWidth,
                        modifier = Modifier.fillMaxWidth()
                    )
                    
                    // 二维码海报 - 绝对定位在背景图右下角（包含在截图内）
                    QRCodePosterCard(
                        modifier = Modifier
                            .align(Alignment.BottomEnd)
                            .padding(bottom = 6.dp, end = 6.dp),
                        data = viewModel.inviteLink
                    )
                }
            }
        }
        
        // 加载弹框
        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.5f)),
                contentAlignment = Alignment.Center
            ) {
                Card(
                    modifier = Modifier.padding(32.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = Color.White
                    ),
                    elevation = CardDefaults.cardElevation(8.dp),
                    shape = RoundedCornerShape(16.dp)
                ) {
                    Column(
                        modifier = Modifier.padding(32.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        CircularProgressIndicator(
                            color = Color(0xFF1E88E5),
                            strokeWidth = 4.dp
                        )
                        Text(
                            text = "保存中...",
                            fontFamily = CaiFont,
                            fontSize = 16.sp,
                            color = Color(0xFF333333)
                        )
                    }
                }
            }
        }
    }
}

/**
 * 二维码海报卡片
 */
@Composable
fun QRCodePosterCard(
    modifier: Modifier = Modifier,
    data: String
) {
    if (data.isNotEmpty()) {
        Card(
            modifier = modifier
                .size(100.dp),
            colors = CardDefaults.cardColors(
                containerColor = Color.White
            ),
            elevation = CardDefaults.cardElevation(12.dp),
            shape = RoundedCornerShape(12.dp)
        ) {
            Box(
                modifier = Modifier
                    .wrapContentSize()
                    .padding(8.dp),
                contentAlignment = Alignment.Center
            ) {
                // 使用 qrose 生成二维码
                val qrCodePainter = rememberQrCodePainter(data) {
                    shapes {
                        ball = QrBallShape.circle()
                        darkPixel = QrPixelShape.roundCorners()
                        frame = QrFrameShape.roundCorners(.25f)
                    }
                    colors {
                        dark = QrBrush.solid(Color(0xFF1E88E5))
                    }
                }

                Image(
                    painter = qrCodePainter,
                    contentDescription = "邀请二维码",
                    modifier = Modifier.fillMaxSize()
                )
            }
        }
    }
}